import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, Observable, catchError, throwError } from 'rxjs';
import { OperationsApiRoutes, OperationsRoutes } from '../../constants/operations.constants';

/**
 * Turns one refusal - the one that means "you cannot use this site at all" -
 * into the screen that explains it.
 *
 * Operations is gated on the server. `AccountService` refuses session creation
 * outright for an account without site access, answering 403 to
 * `GET /api/Account/login` with "Operations access has not been granted or has
 * been revoked."; the sign-in flow then showed that sentence wherever it puts
 * errors. A route guard cannot help, because the refusal happens while the
 * session is being created, before any guard runs.
 *
 * Only that one call is treated this way, deliberately. `OperationsAccessMiddleware`
 * answers 403 on other endpoints too, and an endpoint-level refusal is a
 * different statement: it means this account cannot do this particular thing,
 * not that it cannot be here at all. Redirecting on those would turn a missing
 * permission on one screen into an absolute blocker across the site, so they
 * are left to propagate and be handled where they happen.
 *
 * Matched on the path rather than the full URL because by the time this
 * interceptor runs the request may or may not have been rewritten to an
 * absolute address by the interceptors registered before it.
 */
@Injectable()
export class OperationsAccessInterceptor implements HttpInterceptor {
  private readonly router = inject(Router);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: unknown) => {
        if (!this.isSignInRefusal(request, error)) {
          return throwError(() => error);
        }

        // Already there: guard against stacking identical history entries if
        // the sign-in call is retried.
        if (!this.router.url.startsWith(`/${OperationsRoutes.noAccess}`)) {
          void this.router.navigate(['/', OperationsRoutes.noAccess]);
        }

        // Swallowed rather than rethrown. What the sign-in flow would otherwise
        // do with this - show the API's own sentence - is exactly what the
        // screen we are navigating to replaces.
        return EMPTY;
      }),
    );
  }

  /** A 403 from the sign-in call, and nothing else. */
  private isSignInRefusal(request: HttpRequest<unknown>, error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse) || error.status !== 403) {
      return false;
    }

    const path = request.url.split('?')[0].toLowerCase();
    return path.endsWith(OperationsApiRoutes.accountLogin.toLowerCase());
  }
}
